import { Notifier } from '@airbrake/browser';

interface AirbrakeConfig {
  projectId: number;
  projectKey: string | null;
  environment: string;
}

declare global {
  interface Window {
    AirbrakeConfig: AirbrakeConfig;
  }
}

let notifier: Notifier | null = null;

export function initializeAirbrake(config: AirbrakeConfig) {
  notifier = null;
  if (!config) {
    return null;
  }

  const { projectKey, projectId } = config;

  if (projectKey && projectId) {
    notifier = new Notifier({
      ...config,
      projectKey,
      performanceStats: false,
      instrumentation: {
        onerror: true,
        fetch: true,
        history: true,
        xhr: true,
      },
    });

    addFilters(notifier);
  }

  return notifier;
}

export function notify(err: any) {
  if (notifier) {
    return notifier.notify(err);
  }

  return null;
}

export function addFilters(newNotifier: Notifier) {
  newNotifier.addFilter((notice) => {
    const errorContextHasHistory = notice.context?.history?.length > 0;
    const isIncompleteNetworkRequest = (lastHistory) => lastHistory?.statusCode === 0;

    if (errorContextHasHistory) {
      const lastHistory = notice.context?.history?.[notice.context.history.length - 1];
      if (isIncompleteNetworkRequest(lastHistory)) {
        return null;
      }
      if (safeToIgnoreClientRequestError(lastHistory)) {
        return null;
      }
    }
    return notice;
  });

  newNotifier.addFilter((notice) => {
    const message = notice.errors?.[0]?.message;
    const accessDeniedError = '<Code>AccessDenied</Code><Message>Access Denied</Message>';
    const alertIsNotAFunctionError = '$(...).alert is not a function';

    if (message === alertIsNotAFunctionError || message?.includes(accessDeniedError)) {
      return null;
    }

    return notice;
  });
}

// Ignore 400 code network errors (unauthorized, not found, etc) from tiles or 3rd party
// services (google + esri mainly) which produce these errors as part of their normal function
function safeToIgnoreClientRequestError(lastHistory) {
  const intCode = parseInt(lastHistory?.statusCode ?? '', 10);
  if (intCode >= 400 && intCode < 500) {
    // Do NOT filter requests that failed when trying to talk to web.fulcrumapp.com
    if (intCode === 400 && lastHistory?.url?.indexOf('https://web.fulcrumapp.com') === 0) {
      return false;
    }
    return true;
  }
  return false;
}
